import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  X, Info, History, Pencil, Trash2, ShieldAlert, Truck, ClipboardCheck,
  Loader2, User, Clock, ArrowRight, Wrench, Package, Hammer, Ruler, DollarSign,
  Scissors,
} from "lucide-react";
import SplitLineDialog from "@/components/SplitLineDialog";

const API = process.env.REACT_APP_BACKEND_URL;

const STAGE_TINT = {
  "PO Received":     "#3b82f6",
  "Procurement":     "#f97316",
  "Manufacturing":   "#a855f7",
  "Quality Control": "#14b8a6",
  "Dispatch":        "#f59e0b",
  "Completed":       "#94a3b8",
};

function formatTs(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d)) return iso;
    const _m = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    return `${String(d.getDate()).padStart(2,"0")}-${_m[d.getMonth()]}-${String(d.getFullYear()).slice(-2)}, ${String(d.getHours()).padStart(2,"0")}:${String(d.getMinutes()).padStart(2,"0")}`;
  } catch { return iso; }
}

function formatCurrency(usd) {
  if (usd === null || usd === undefined) return "—";
  return `$${Number(usd).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function StageBadge({ stage }) {
  const c = STAGE_TINT[stage] || "#94a3b8";
  return (
    <span
      className="inline-block text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded"
      style={{ color: c, background: c + "1a", border: `1px solid ${c}33` }}
    >
      {stage || "—"}
    </span>
  );
}

/**
 * LineItemDetailsModal — single source of truth for "what happened to this item"
 *
 * Props:
 *   item              the line-item object (already in client state)
 *   open              boolean
 *   onClose           () => void
 *   onEditClick       optional — surface "Edit" button
 *   onDeleteClick     optional — surface "Delete" button
 *   canEdit           boolean
 *   canDelete         boolean
 */
export default function LineItemDetailsModal({
  item, open, onClose, onEditClick, onDeleteClick, canEdit, canDelete,
  allItems, onSplitSuccess,
}) {
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const [splitOpen, setSplitOpen] = useState(false);

  useEffect(() => {
    if (!open || !item?.id) return;
    let cancelled = false;
    setLoadingHistory(true);
    setErrMsg("");
    axios.get(`${API}/api/changelog`, { params: { item_id: item.id, limit: 200 } })
      .then(res => { if (!cancelled) setHistory(Array.isArray(res.data) ? res.data : []); })
      .catch(e => { if (!cancelled) setErrMsg(e?.response?.data?.detail || "Could not load history"); })
      .finally(() => { if (!cancelled) setLoadingHistory(false); });
    return () => { cancelled = true; };
  }, [open, item?.id]);

  // Group history entries by stage so the timeline reads as a stage-by-stage story
  const groupedHistory = useMemo(() => {
    if (!history.length) return [];
    const groups = new Map();
    for (const h of history) {
      const key = h.stage || "—";
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(h);
    }
    // Newest activity first within each group; sort groups by their latest entry timestamp
    const out = Array.from(groups.entries()).map(([stage, entries]) => ({
      stage,
      entries: entries.sort((a, b) => (b.timestamp || "").localeCompare(a.timestamp || "")),
      latest: entries[0]?.timestamp || "",
    }));
    out.sort((a, b) => (b.latest || "").localeCompare(a.latest || ""));
    return out;
  }, [history]);

  if (!item) return null;

  const totalSpend = (item.material_spend || 0) + (item.tooling_spend || 0) + (item.gauging_spend || 0);
  // Procurement record is shown whenever there's *any* procurement signal on
  // the item — keeps the historical sub-team status + spend visible after the
  // item has moved on to Manufacturing / QC / Dispatch / Completed.
  const hasProcurementRecord = (
    totalSpend > 0 ||
    !!item.material_status || !!item.tooling_status || !!item.gauging_status ||
    !!item.material_eta    || !!item.tooling_eta    || !!item.gauging_eta
  );

  return (
    <>
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        className="bg-[hsl(var(--card))] border-[hsl(var(--border))] w-[95vw] md:w-[760px] max-w-[95vw] p-0 overflow-hidden flex flex-col max-h-[90vh]"
        data-testid="line-item-details-modal"
      >
        {/* Stage-tinted top bar */}
        <div className="h-[3px] w-full" style={{ background: STAGE_TINT[item.stage] || "#94a3b8" }} />

        {/* Header */}
        <DialogHeader className="px-5 pt-4 pb-3 shrink-0 border-b border-[hsl(var(--border))]/50">
          <div className="flex items-start gap-3">
            <span
              className="shrink-0 w-9 h-9 rounded-lg flex items-center justify-center"
              style={{ background: (STAGE_TINT[item.stage] || "#94a3b8") + "1a" }}
            >
              <Info className="w-4 h-4" style={{ color: STAGE_TINT[item.stage] || "#94a3b8" }} />
            </span>
            <div className="flex-1 min-w-0">
              <DialogTitle className="flex items-center gap-2 text-base leading-tight">
                <span className="truncate">{item.item_description || "Line item"}</span>
              </DialogTitle>
              <DialogDescription className="text-[11px] mt-1 flex items-center gap-2 flex-wrap">
                <span className="font-mono-data px-1.5 py-0.5 rounded bg-[hsl(var(--muted))] text-[hsl(var(--foreground))] font-bold">
                  <span className="text-[8px] font-bold uppercase tracking-widest text-[hsl(var(--muted-foreground))] mr-1">PO</span>{item.po_number || "—"}
                </span>
                {item.line_number && (
                  <span className="font-mono-data px-1.5 py-0.5 rounded bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]">
                    <span className="text-[8px] font-bold uppercase tracking-widest mr-1">LINE</span>{item.line_number}
                  </span>
                )}
                <StageBadge stage={item.stage} />
                {item.client && <span className="text-[hsl(var(--muted-foreground))]">· {item.client}</span>}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          {/* ── Line details block ──────────────────────────────────────── */}
          <section data-testid="li-details-summary">
            <h3 className="text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--muted-foreground))] mb-2 flex items-center gap-1.5">
              <Package className="w-3 h-3" />Details
            </h3>
            <dl className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-3 text-[12px]">
              <DetailField label="Part #">{item.part_number || "—"}</DetailField>
              <DetailField label="ERP Code" mono accent={item.erp_code ? "#10b981" : null}>{item.erp_code || "—"}</DetailField>
              <DetailField label="Revision">{item.revision || "—"}</DetailField>
              <DetailField label="Quantity" mono>{item.quantity ? `${item.quantity}` : "—"}</DetailField>
              <DetailField label="Value" mono>{formatCurrency(item.value_usd)}</DetailField>
              {/* Show Spent whenever there is procurement spend recorded — even
                  after the item has moved past Procurement. Backend strips
                  these values when the viewer lacks can_view_value. */}
              {("material_spend" in item || "tooling_spend" in item || "gauging_spend" in item) && (
                <DetailField label="Spent" mono accent={totalSpend > 0 ? "#10b981" : null}>
                  {formatCurrency(totalSpend)}
                </DetailField>
              )}
              <DetailField label="Priority">{item.priority || "—"}</DetailField>
              <DetailField label="Dispatch Due" mono>{item.dispatch_due_date || "—"}</DetailField>
              <DetailField label="Created" mono>{formatTs(item.created_at)}</DetailField>
              <DetailField label="Last Updated" mono>{formatTs(item.updated_at)}</DetailField>
            </dl>
          </section>

          {/* ── Procurement subteam status ─── (shown if the item carries any
              procurement record — current or historical) ─── */}
          {hasProcurementRecord && (
            <section data-testid="li-details-proc">
              <h3 className="text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--muted-foreground))] mb-2 flex items-center gap-1.5">
                <Hammer className="w-3 h-3" />
                {item.stage === "Procurement" ? "Procurement Sub-teams" : "Procurement Record"}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <SubteamCard label="Material" Icon={Package} status={item.material_status} eta={item.material_eta} spend={item.material_spend} />
                <SubteamCard label="Tooling"  Icon={Wrench}  status={item.tooling_status}  eta={item.tooling_eta}  spend={item.tooling_spend} />
                <SubteamCard label="Gauging"  Icon={Ruler}   status={item.gauging_status}  eta={item.gauging_eta}  spend={item.gauging_spend} />
              </div>
            </section>
          )}

          {/* ── Block / Rework state ───────────────────────────────────── */}
          {(item.is_blocked || item.is_rework) && (
            <section>
              <h3 className="text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--muted-foreground))] mb-2 flex items-center gap-1.5">
                <ShieldAlert className="w-3 h-3" />Flags
              </h3>
              {item.is_blocked && (
                <div className="px-3 py-2 rounded-md border border-rose-500/30 bg-rose-500/5 text-[11px]" data-testid="li-blocked-flag">
                  <span className="font-bold text-rose-600 uppercase tracking-widest text-[10px] mr-2">Blocked</span>
                  <span className="text-[hsl(var(--foreground))]">{item.block_reason || "—"}</span>
                  {item.next_action && (
                    <div className="mt-1.5 pt-1.5 border-t border-rose-500/20 text-[hsl(var(--foreground))]">
                      <span className="text-[9px] uppercase tracking-widest font-bold text-amber-600 mr-1.5">Next</span>
                      <span>{item.next_action}</span>
                      <span className="text-[hsl(var(--muted-foreground))] font-mono-data ml-1">· by {item.next_action_date}</span>
                      {item.next_action_by && (
                        <div className="text-[10px] text-[hsl(var(--muted-foreground))] mt-0.5">
                          Owner: <span className="font-semibold text-[hsl(var(--foreground))]">{item.next_action_by}</span>{item.next_action_by_role && ` (${item.next_action_by_role})`}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              {item.is_rework && (
                <div className="px-3 py-2 mt-2 rounded-md border border-teal-500/30 bg-teal-500/5 text-[11px]" data-testid="li-rework-flag">
                  <span className="font-bold text-teal-600 uppercase tracking-widest text-[10px] mr-2">Rework</span>
                  <span className="text-[hsl(var(--foreground))]">{item.rework_reason || "Returned for rework"}</span>
                </div>
              )}
            </section>
          )}

          {/* ── Timeline ───────────────────────────────────────────────── */}
          <section data-testid="li-details-history">
            <h3 className="text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--muted-foreground))] mb-2 flex items-center gap-1.5">
              <History className="w-3 h-3" />Timeline
            </h3>
            {loadingHistory && (
              <div className="flex items-center gap-2 text-[11px] text-[hsl(var(--muted-foreground))]">
                <Loader2 className="w-3 h-3 animate-spin" /> Loading history…
              </div>
            )}
            {errMsg && <p className="text-[11px] text-rose-500" data-testid="li-history-error">{errMsg}</p>}
            {!loadingHistory && !errMsg && groupedHistory.length === 0 && (
              <p className="text-[11px] text-[hsl(var(--muted-foreground))] italic">No recorded actions yet.</p>
            )}
            <div className="space-y-3">
              {groupedHistory.map(g => (
                <div key={g.stage} data-testid={`li-history-stage-${g.stage}`}>
                  <div className="flex items-center gap-2 mb-1.5">
                    <StageBadge stage={g.stage} />
                    <span className="text-[10px] text-[hsl(var(--muted-foreground))]">{g.entries.length} action{g.entries.length === 1 ? "" : "s"}</span>
                  </div>
                  <ol className="relative pl-4 border-l border-[hsl(var(--border))] space-y-2">
                    {g.entries.map(e => (
                      <li key={e.id} className="relative">
                        <div className="flex items-baseline justify-between gap-2 flex-wrap">
                          <p className="text-[12px] font-medium text-[hsl(var(--foreground))]">
                            {e.action}
                            {e.old_value && e.new_value && (
                              <span className="text-[hsl(var(--muted-foreground))] text-[11px] font-normal ml-1.5">
                                <span className="line-through opacity-60">{e.old_value}</span>
                                <ArrowRight className="inline w-3 h-3 mx-1 opacity-60" />
                                <span className="font-semibold text-[hsl(var(--foreground))]">{e.new_value}</span>
                              </span>
                            )}
                            {!e.old_value && e.new_value && (
                              <span className="text-[hsl(var(--muted-foreground))] text-[11px] font-normal ml-1.5">{e.new_value}</span>
                            )}
                          </p>
                          <span className="text-[10px] font-mono-data text-[hsl(var(--muted-foreground))] flex items-center gap-1 shrink-0">
                            <Clock className="w-2.5 h-2.5" />{formatTs(e.timestamp)}
                          </span>
                        </div>
                        <p className="text-[10px] text-[hsl(var(--muted-foreground))] flex items-center gap-1 mt-0.5">
                          <User className="w-2.5 h-2.5" />
                          <span className="font-medium">{e.user || "Unknown"}</span>
                          {e.user_role && <span className="opacity-70">· {e.user_role}</span>}
                        </p>
                      </li>
                    ))}
                  </ol>
                </div>
              ))}
            </div>
          </section>
        </div>

        {/* Footer actions */}
        <div className="px-5 py-3 flex items-center justify-between gap-2 border-t border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30 shrink-0">
          <div className="flex items-center gap-2">
            {canEdit && onEditClick && (
              <button
                onClick={() => { onEditClick(item); onClose(); }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold text-white bg-orange-600 hover:bg-orange-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500"
                data-testid="li-edit-btn"
              >
                <Pencil className="w-3 h-3" />Edit
              </button>
            )}
            {canEdit && (
              <button
                onClick={() => setSplitOpen(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold text-emerald-700 dark:text-emerald-400 hover:bg-emerald-500/10 border border-emerald-500/30 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
                data-testid="li-split-btn"
              >
                <Scissors className="w-3 h-3" />Split line
              </button>
            )}
            {canDelete && onDeleteClick && (
              <button
                onClick={() => { onDeleteClick(item.id); onClose(); }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold text-rose-600 hover:bg-rose-500/10 border border-rose-500/30 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-500"
                data-testid="li-delete-btn"
              >
                <Trash2 className="w-3 h-3" />Delete
              </button>
            )}
          </div>
          <button onClick={onClose} className="text-xs font-semibold text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]" data-testid="li-close-btn">
            Close
          </button>
        </div>
      </DialogContent>
    </Dialog>
    <SplitLineDialog
      open={splitOpen}
      onOpenChange={setSplitOpen}
      item={item}
      allItems={allItems || []}
      onSplitSuccess={(child) => {
        setSplitOpen(false);
        onClose();
        onSplitSuccess?.(child);
      }}
    />
    </>
  );
}

function DetailField({ label, mono, accent, children }) {
  return (
    <div>
      <dt className="text-[9px] uppercase tracking-widest font-bold text-[hsl(var(--muted-foreground))]">{label}</dt>
      <dd
        className={`text-[12px] mt-0.5 ${mono ? "font-mono-data tabular-nums" : ""} font-medium`}
        style={accent ? { color: accent } : {}}
      >
        {children}
      </dd>
    </div>
  );
}

function SubteamCard({ label, Icon, status, eta, spend }) {
  const tone = (status === "Complete") ? "#10b981"
             : (status === "Ordered") ? "#3b82f6"
             : (status === "Partially Received") ? "#f59e0b"
             : "#64748b";
  return (
    <div className="px-3 py-2.5 rounded-md border bg-[hsl(var(--muted))]/30" style={{ borderColor: tone + "30" }}>
      <div className="flex items-center gap-1.5 mb-1.5">
        <Icon className="w-3 h-3" style={{ color: tone }} />
        <span className="text-[10px] uppercase tracking-widest font-bold" style={{ color: tone }}>{label}</span>
      </div>
      <p className="text-[12px] font-semibold text-[hsl(var(--foreground))]">{status || "Pending"}</p>
      <div className="mt-1 text-[10px] text-[hsl(var(--muted-foreground))] space-y-0.5">
        {status === "Ordered" && (
          <p>ETA · <span className="font-mono-data font-bold">{eta || "N/A"}</span></p>
        )}
        {(spend || 0) > 0 && (
          <p>Spent · <span className="font-mono-data font-bold text-emerald-600">${Number(spend).toLocaleString("en-US", { maximumFractionDigits: 0 })}</span></p>
        )}
      </div>
    </div>
  );
}
